import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Typography,
  Space,
  Image,
  Tooltip,
  notification,
  Button,
} from "antd";
import { useHistory } from "react-router";
import Styles from "./TripSchedule.module.scss";
import { backArrowIcon } from "../../../assets/images";
import {
  TTrip,
  TripType,
  TripBothAddress,
  TripTypesButton,
  AddressType,
  TTripAddress,
  RideRequest,
  ITripRulesValidationInput,
  IRulesValidationResponse,
  ITripCostRequest,
  ITripCostResponse,
  WandaTripCost,
  IWandaServiceArea,
  ButtonTypes,
  ButtonTypeDefaultValue,
  WheelchairUpdate,
} from "../../../types/TripTypes";
import {
  getServiceusersInfoByServiceTakerId,
  TripRulesValidation,
  GetCostDetailsByAddressList,
  getWandaServiceArea,
  getAutoComplete,
  getPlaceDetails,
  UpdatePersonAttributesKey,
} from "../../../services";
import { useQuery, useMutation } from "@tanstack/react-query";
import Trip from "./Trip/Trip";
import { useWindowSize } from "../../../hooks/useWindowSize";
import {
  GeneralDefinition,
  RegistrationEnums,
  TripReasonEnum,
} from "../../../types/ApplicationTypes";
import { useTripsCtx } from "../../../context/TripDetailsContext";
import moment from "moment";
import GoogleLoader from "../../../assets/Loader/GoogleSvgLoader/GoogleLoader";
import { tripScheduleWarningMessage } from "../../../helpers/tripScheduleWarningMessage";
import NoReturnTripPopup from "./NoReturnTripPopup/NoReturnTripPopup";
import RemoveWheelchairPopup from "./RemoveWheelchairPopup/RemoveWheelchairPopup";
import SameAddressPopup from "./SameAddressPopup/SameAddressPopup";
import NotCoverageZipPopup from "./NotCoverageZipPopup/NotCoverageZipPopup";
import { usePersonSummaryCtx } from "../../../context/PersonSummaryContext";
import NotPastTripPopup from "./NotPastTripPopup/NotPastTripPopup";

type Props = {
  params: { careTakerId: string; serviceUserId: string };
  date: string | null;
};

const TripSchedule: React.FC<Props> = ({ params, date }) => {
  const history = useHistory();
  const { width } = useWindowSize();
  const [trips, setTrips] = useState<TTrip[]>([]);
  const [btnAllEnable, setBtnAllEnable] = useState<ButtonTypes>(
    ButtonTypeDefaultValue
  );
  const [isRemoveWheelchair, setIsRemoveWheelchair] = useState<boolean>(false);
  const [isNotPastTrip, setIsNotPastTrip] = useState<boolean>(false);
  const [isNotPastTripStatus, setIsNotPastTripStatus] = useState<boolean>(
    false
  );
  const [isReturnTripNotAdded, setIsReturnTripNotAdded] = useState<boolean>(
    false
  );

  const {
    tripSetupDetailsCtx,
    modeSelectionBackBtnCtx,
    tripValidationIdCtx,
    tripDateCtx,
    wheelChairCtx,
    wandaTripInfoCtx,
    rebookTripCtx,
    isRebookCtx,
    isLoadingCostDetailsCtx,
    wandaOrgId,
    uspsFieldValidationErrorCtx,
  } = useTripsCtx();
  const { caregiverSummaryInfo, isWandaConcierge } = usePersonSummaryCtx();
  const [tripCreationDetails, setTripCreationDetails] = tripSetupDetailsCtx;
  const { wandaServiceAreaCtx, handleManageServiceUsersData } = useTripsCtx();
  const [wandaService, setWandaService] = wandaServiceAreaCtx;
  const [
    isModeSelectionBackBtnClicked,
    setIsModeSelectionBackBtnClicked,
  ] = modeSelectionBackBtnCtx;
  const [, setRulesValidationId] = tripValidationIdCtx;
  const [, settripDate] = tripDateCtx;
  const [, setWheelChair] = wheelChairCtx;
  const [, setWandaTripInfo] = wandaTripInfoCtx;
  const [isRebook, setIsRebook] = isRebookCtx;
  const [rebookTrip] = rebookTripCtx;
  const [, setIsLoadingCostDetails] = isLoadingCostDetailsCtx;
  const [
    uspsFieldValidationError,
    setUspsFieldValidationError,
  ] = uspsFieldValidationErrorCtx;
  const [isNotCovergeZip, setIsNotCovergeZip] = useState<boolean>(false);
  const [isNotSameAddress, setisNotSameAddress] = useState<boolean>(false);
  const isValidationError =
    !uspsFieldValidationError.pickup && !uspsFieldValidationError.dropOff;

  window.addEventListener("load", () => history.push(`/DashBoard`));

  const serviceUserData = useQuery(
    [
      "getServiceusersInfoByServiceTakerId",
      params.careTakerId,
      params.serviceUserId,
    ],
    () =>
      getServiceusersInfoByServiceTakerId(
        Number(params.careTakerId),
        Number(params.serviceUserId)
      )
  );

  const tripRulesValidation = useMutation(
    async (
      rulesValiationData: ITripRulesValidationInput
    ): Promise<IRulesValidationResponse> =>
      TripRulesValidation(rulesValiationData),
    {
      onError: (error: Error) => {
        if (error?.message === "InvalidDate") {
          if (isWandaConcierge) {
            setIsNotPastTrip(true);
          }
        } else {
          notification.error({
            message: "Error",
            description:
              "Failed to fetch Rules Validation Id. Please try again later.",
          });
        }
      },

      onSuccess: (data) => {
        if (isNotPastTripStatus) {
          setIsReturnTripNotAdded(true);
        }

        if (data.rulesValidationId !== undefined) {
          trips?.forEach((element: TTrip, index) => {
            element.distanceInMiles = String(data.services[index]?.miles);
          });
          handleFetchCostDetails(trips);

          setRulesValidationId(data.rulesValidationId);
          settripDate(date);
          setWandaService([]);
          const nextPageLink = `/DashBoard/Transportation/ModeSelection/${params.serviceUserId}`;
          handleManageServiceUsersData();
          if (!isNotPastTripStatus) {
            history.push(nextPageLink);
          }
        } else {
          notification.error({
            message: "Error",
            description:
              "Failed to fetch Rules Validation Id. Please try again later.",
          });
        }
      },
    }
  );

  const handleBackBtnClick = () => {
    setTrips([]);
    setWandaService([]);
    setIsModeSelectionBackBtnClicked(false);
    rebookTrip.length && setIsRebook(true);
    history.push(
      `/DashBoard/ScheduleService/ScheduleServiceCalender/${params.careTakerId}/${params.serviceUserId}`
    );
  };

  useEffect(() => {
    if (serviceUserData.data && !isModeSelectionBackBtnClicked) {
      setWheelChair(serviceUserData.data.wandaAccommodations);
      if (serviceUserData.data.personAttributes.homeAddress && !isRebook) {
        let fullAddress = AddressDisplay(
          serviceUserData.data.personAttributes.homeAddress
        );

        const getPlaceId = async () => {
          try {
            const placeId = await getAutoComplete(fullAddress);
            const results = await getPlaceDetails(
              placeId?.predictions[0]?.place_id,
              placeId?.predictions[0]?.description,
            );
            let lat = results?.results[0]?.lat;
            let lng = results?.results[0]?.lng;

            const newTrip: TTrip[] = [
              {
                id: 1,
                tripType: TripTypesButton.Inital,
                tripSelectedTypeDefault: TripType.LeaveAt,

                pickupAddressDefault: {
                  ...serviceUserData.data.personAttributes.homeAddress,
                  isHomeAddress: true,
                  latitude: lat,
                  longitude: lng,
                },
                firstName: serviceUserData.data.firstName,
                wheelChairTravel:
                  serviceUserData.data.wandaAccommodations &&
                  serviceUserData.data.wandaAccommodations.wheelChairTravel
                    ? true
                    : false,
              },
            ];
            if (!isRebook) {
              fetchServiceArea(
                serviceUserData.data.personAttributes.homeAddress.zip,
                newTrip
              );
            }
            setTrips(newTrip);
          } catch (error) {
            notification.error({
              message: "Error",
              description: "Failed to Fetch Place Details.",
            });
          }
        };

        getPlaceId();
      }
    }
  }, [serviceUserData.data]);

  async function fetchServiceArea(zip: string, trips: TTrip[]) {
    setWandaService([]);
    try {
      await getServiceArea(zip, 0, trips);
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to Fetch Service Area.",
      });
    }
  }

  const handleChangeAddress = (
    index: number,
    allAddress: TripBothAddress
  ): void => {
    if (allAddress.pickup !== undefined) {
      const newTrip = [...trips];
      if (newTrip[index].pickupAddressDefault !== undefined) {
        allAddress.pickup.driverInstruction =
          trips[index]?.pickupAddressDefault?.driverInstruction;
      }
      newTrip[index].pickupAddressDefault = allAddress.pickup.address1
        ? allAddress.pickup
        : undefined;
      setTrips(newTrip);
    } else if (allAddress.dropOff !== undefined) {
      const newTrip = [...trips];
      if (newTrip[index].dropOffAddressDefault !== undefined) {
        allAddress.dropOff.driverInstruction =
          trips[index]?.dropOffAddressDefault?.driverInstruction;
      }
      newTrip[index].dropOffAddressDefault = allAddress.dropOff.address1
        ? allAddress.dropOff
        : undefined;
      setTrips(newTrip);
    }
  };
  const AddressDisplay = (address: TTripAddress): string => {
    const { address1, address2, city, state, zip } = address;
    const finalAddress =
      address1 && address2
        ? [`${address1} ${address2}`, city, state, zip]
        : [address1, city, state, zip];
    const fullAddress = finalAddress.filter(Boolean).join(", ");

    return fullAddress || "";
  };

  const handleTripDelete = (index: number) => {
    const isCallselected = trips[index + 1]?.isWillCall ? true : false;

    if (trips.length - 1 != index) {
      if (isCallselected) {
        handleWillCallChanged(index + 1);
      } else {
        handleClearTime(index + 1);
      }
    }

    const updatedTrips = trips.filter((item, i) => i !== index);
    const warningTrips = tripScheduleWarningMessage(updatedTrips);
    setWandaService([])
    setTrips(warningTrips);
  };

  const handleChangeTrip = (index: number, tripType: string) => {
    const newTrip = [...trips];
    newTrip[index].tripSelectedTypeDefault =
      tripType === TripType.ArrivedBy ? TripType.ArrivedBy : TripType.LeaveAt;
    setTrips(newTrip);
  };

  const handleChangeTime = (index: number, time: string) => {
    const newTrip = [...trips];
    newTrip[index].scheduleTime = time;
    if (newTrip[index].tripSelectedTypeDefault === TripType.ArrivedBy) {
      newTrip[index].appointmentTime = `1900-01-01T${moment(
        time,
        "h:mm a"
      ).format("HH:mm:ss")}`;
    } else {
      newTrip[index].pickTime = `1900-01-01T${moment(time, "h:mm a").format(
        "HH:mm:ss"
      )}`;
    }
    setTrips(newTrip);
    const warnTrips = tripScheduleWarningMessage(trips);
    setTrips(warnTrips);
  };

  const handleClearTime = (index: number) => {
    const newTrip = [...trips];
    newTrip[index].areTimesChronological =
      newTrip[index].areTimesChronological && false;
    newTrip[index].scheduleTime = undefined;
    setTrips(newTrip);
  };

  const handleClearFields = (index: number, clearType: string) => {
    const undefinedAddress: TTripAddress = {
      address1: "",
      address2: "",
      city: "",
      state: "",
      zip: "",
      strAddress: undefined,
    };
    if (clearType === AddressType.Pickup) {
      const newTrip = [...trips];
      if (newTrip && newTrip.length > 0) {
        newTrip[index].pickupAddressDefault = {
          ...undefinedAddress,
          driverInstruction:
            trips[index].pickupAddressDefault?.driverInstruction !== undefined
              ? trips[index].pickupAddressDefault?.driverInstruction
              : undefined,
        };
        setTrips(newTrip);
      }
    } else if (clearType === AddressType.DropOff) {
      const newTrip = [...trips];
      if (newTrip && newTrip.length > 0) {
        newTrip[index].dropOffAddressDefault = {
          ...undefinedAddress,
          driverInstruction:
            trips[index].dropOffAddressDefault?.driverInstruction !== undefined
              ? trips[index].dropOffAddressDefault?.driverInstruction
              : undefined,
        };
        setTrips(newTrip);
      }
    }
  };

  const handleFieldChange = (
    index: number,
    name: string,
    value: string,
    field: string
  ) => {
    const updatedField =
      field === AddressType.Pickup
        ? "pickupAddressDefault"
        : "dropOffAddressDefault";
    setTrips((prevTrips) => {
      const updatedTrips = [...prevTrips];
      const tripToUpdate = { ...updatedTrips[index] };
      tripToUpdate[updatedField] = {
        ...tripToUpdate[updatedField],
        [name]: value,
      };
      updatedTrips[index] = tripToUpdate;
      return updatedTrips;
    });
  };

  const handleClearinstructionField = (index: number, field: string) => {
    const updatedField =
      field === AddressType.Pickup
        ? "pickupAddressDefault"
        : "dropOffAddressDefault";
    setTrips((prevTrips) => {
      const updatedTrips = [...prevTrips];
      const tripToUpdate = { ...updatedTrips[index] };
      tripToUpdate[updatedField] = {
        ...tripToUpdate[updatedField],
        driverInstruction: undefined,
      };
      updatedTrips[index] = tripToUpdate;
      return updatedTrips;
    });
  };

  const handleDriverInstrctionValidation = (
    index: number,
    field: string,
    value: boolean
  ) => {
    const updatedField =
      field === AddressType.Pickup
        ? "pickupAddressDefault"
        : "dropOffAddressDefault";
    setTrips((prevTrips) => {
      const updatedTrips = [...prevTrips];
      const tripToUpdate = { ...updatedTrips[index] };
      tripToUpdate[updatedField] = {
        ...tripToUpdate[updatedField],
        isDriverInStructionEnabled: value,
      };
      updatedTrips[index] = tripToUpdate;
      return updatedTrips;
    });
  };

  const handleAddTrip = async (
    fieldType: "stop" | "return" | "continue",
    isFinal?: boolean
  ) => {
    if (trips.length < 3) {
      if (fieldType === "stop") {
        const pastTrip = trips[trips.length - 1];
        const newTrip = [...trips];
        const trip: TTrip = {
          id: trips.length + 1,
          tripType:
            fieldType === "stop"
              ? TripTypesButton.AddStop
              : TripTypesButton.ReturnTrip,
          tripSelectedTypeDefault: TripType.LeaveAt,
          pickupAddressDefault: {
            ...pastTrip.dropOffAddressDefault,
            driverInstruction: undefined,
          },
          firstName: pastTrip.firstName && pastTrip.firstName,
          wheelChairTravel:
            pastTrip.wheelChairTravel && pastTrip.wheelChairTravel,
          areTimesChronological: false,
        };
        let isSameMarketArea = await getServiceArea(
          String(trips[trips.length - 1].dropOffAddressDefault?.zip),
          trips.length - 1,
          trips
        );
        if (!isSameMarketArea) {
          newTrip.push(trip);
          setTrips(newTrip);
        }
      } else if (fieldType === "return") {
        const pastTrip = trips[trips.length - 1];
        const newTrip = [...trips];
        const trip: TTrip = {
          id: trips.length + 1,
          tripType:
            fieldType === "return"
              ? TripTypesButton.ReturnTrip
              : TripTypesButton.AddStop,
          tripSelectedTypeDefault: TripType.LeaveAt,
          pickupAddressDefault: {
            ...pastTrip.dropOffAddressDefault,
            driverInstruction: undefined,
          },
          dropOffAddressDefault: {
            ...trips[0].pickupAddressDefault,
            isHomeAddress: true,
            driverInstruction: undefined,
          },
          firstName: pastTrip.firstName && pastTrip.firstName,
          wheelChairTravel:
            pastTrip.wheelChairTravel && pastTrip.wheelChairTravel,
          areTimesChronological: false,
        };
        let isSameMarketArea = await getServiceArea(
          String(trips[trips.length - 1].dropOffAddressDefault?.zip),
          trips.length - 1,
          trips
        );
        if (!isSameMarketArea) {
          newTrip.push(trip);
          setTrips(newTrip);
        }
      } else if (fieldType === "continue") {
        let isSameMarketArea = await getServiceArea(
          String(trips[trips.length - 1].dropOffAddressDefault?.zip),
          trips.length - 1,
          trips
        );
        if (!isSameMarketArea) {
          if (trips[0].id === 2) {
            trips[0].id = trips.length;
            trips[0].tripType = TripTypesButton.Inital;
          }
          if (trips.length <= 2) {
            const isReturnTrip = trips.filter(
              (item) => item.tripType === TripTypesButton.ReturnTrip
            );
            if (isReturnTrip.length !== 1 && isFinal === undefined) {
              setIsNotPastTripStatus(true);
              if (isWandaConcierge) {
                createRulesValidation();
              } else {
                setIsReturnTripNotAdded(true);
              }
            } else if (isFinal !== undefined && isFinal) {
              createRulesValidation();
            } else if (isReturnTrip.length === 1) {
              createRulesValidation();
            }
          }
          setTripCreationDetails(trips);
        }
      }
    } else if (fieldType === "continue") {
      let isSameMarketArea = await getServiceArea(
        String(trips[trips.length - 1].dropOffAddressDefault?.zip),
        trips.length - 1,
        trips
      );
      if (!isSameMarketArea) {
        const isReturnTrip = trips.filter(
          (item) => item.tripType === TripTypesButton.ReturnTrip
        );
        if (isReturnTrip.length !== 1 && isFinal === undefined) {
          setIsReturnTripNotAdded(true);
        } else if (isFinal !== undefined) {
          createRulesValidation();
        } else if (
          trips.length === 3 &&
          isReturnTrip.length === 1 &&
          isFinal === undefined
        ) {
          createRulesValidation();
        }
        setTripCreationDetails(trips);
      }
    }
  };

  const getServiceArea = async (
    zipCode: string,
    index: number,
    trips: TTrip[]
  ): Promise<boolean> => {
    var isReturn = false;
    //Same address check
    var isSameAddress = false;
    trips?.forEach((trip) => {
      const pickupAddress = trip?.pickupAddressDefault;
      const dropAddress = trip?.dropOffAddressDefault;
      if (
        !isSameAddress &&
        pickupAddress !== undefined &&
        dropAddress !== undefined
      ) {
        const pick = AddressDisplay(pickupAddress as TTripAddress);
        const drop = AddressDisplay(dropAddress as TTripAddress);
        if (pick.replaceAll(" ", "") === drop.replaceAll(" ", "")) {
          setisNotSameAddress(true);
          isSameAddress = true;
          handleClearFields(trip.id - 1, AddressType.DropOff);
        } else {
          setisNotSameAddress(false);
          isSameAddress = false;
        }
      }
    });
    if (!isSameAddress) {
      if (wandaService?.length === 0) {
        let serviceArea: IWandaServiceArea[] = await getWandaServiceArea(
          zipCode
        );
        if (serviceArea?.length === 0) {
          setIsNotCovergeZip(true);
          isReturn = true;
        } else {
          setIsNotCovergeZip(false);
          isReturn = false;
        }
        setWandaService(serviceArea);
        if (!serviceArea.find((e: any) => e.zipCode === zipCode)) {
          setIsNotCovergeZip(true);
          isReturn = true;
        } else {
          setIsNotCovergeZip(false);
          isReturn = false;
        }
      } else if (!wandaService.find((e: any) => e.zipCode === zipCode)) {
        setIsNotCovergeZip(true);
        isReturn = true;
      } else {
        setIsNotCovergeZip(false);
        isReturn = false;
      }
      // Additional condition check for pickup address update at last momentum
      if (isReturn) {
        handleClearFields(index, AddressType.DropOff);
      } else if (trips.length > 0) {
        trips?.forEach((trip) => {
          let pickZipCode = trip?.pickupAddressDefault?.zip;
          let dropZipCode = trip?.dropOffAddressDefault?.zip;
          if (
            wandaService?.length > 0 &&
            !isReturn &&
            !wandaService.find((e: any) => e.zipCode === pickZipCode)
          ) {
            setIsNotCovergeZip(true);
            isReturn = true;
            handleClearFields(trip.id - 1, AddressType.Pickup);
          } else if (
            wandaService?.length > 0 &&
            !isReturn &&
            !wandaService.find((e: any) => e.zipCode === dropZipCode)
          ) {
            setIsNotCovergeZip(true);
            isReturn = true;
            handleClearFields(trip.id - 1, AddressType.DropOff);
          } else if (!isReturn) {
            setIsNotCovergeZip(false);
            isReturn = false;
          }
        });
      }
    }
    return isReturn || isSameAddress;
  };

  const handleCancelCoverage = () => {
    setIsNotCovergeZip(false);
  };
  const handleCancelSameAddress = () => {
    setisNotSameAddress(false);
  };

  const createRulesValidation = () => {
    const rideRequestList: RideRequest[] = [];
    trips.forEach((tripsValue) => {
      let filter: RideRequest = {
        rideReasonId: TripReasonEnum.Wanda,
        scheduledTime: date ? moment(date).format("YYYY-MM-DD") : "",
        dropOffAddress: {
          address1: tripsValue.dropOffAddressDefault?.address1
            ? tripsValue.dropOffAddressDefault?.address1.trim()
            : "",
          address2: tripsValue.dropOffAddressDefault?.address2
            ? tripsValue.dropOffAddressDefault?.address2.trim()
            : "",
          city: tripsValue.dropOffAddressDefault?.city
            ? tripsValue.dropOffAddressDefault?.city.trim()
            : "",
          state: tripsValue.dropOffAddressDefault?.state
            ? tripsValue.dropOffAddressDefault?.state.trim()
            : "",
          zipCode: tripsValue.dropOffAddressDefault?.zip
            ? tripsValue.dropOffAddressDefault?.zip.trim()
            : "",
          county: tripsValue.dropOffAddressDefault?.county
            ? tripsValue.dropOffAddressDefault?.county.trim()
            : "",
          latitude: tripsValue.dropOffAddressDefault?.latitude
            ? Number(tripsValue.dropOffAddressDefault?.latitude)
            : 0,
          longitude: tripsValue.dropOffAddressDefault?.longitude
            ? Number(tripsValue.dropOffAddressDefault?.longitude)
            : 0,
        },
        pickupAddress: {
          address1: tripsValue.pickupAddressDefault?.address1
            ? tripsValue.pickupAddressDefault?.address1.trim()
            : "",
          address2: tripsValue.pickupAddressDefault?.address2
            ? tripsValue.pickupAddressDefault?.address2.trim()
            : "",
          city: tripsValue.pickupAddressDefault?.city
            ? tripsValue.pickupAddressDefault?.city.trim()
            : "",
          state: tripsValue.pickupAddressDefault?.state
            ? tripsValue.pickupAddressDefault?.state.trim()
            : "",
          zipCode: tripsValue.pickupAddressDefault?.zip
            ? tripsValue.pickupAddressDefault?.zip.trim()
            : "",
          county: tripsValue.pickupAddressDefault?.county
            ? tripsValue.pickupAddressDefault?.county.trim()
            : "",
          latitude: tripsValue.pickupAddressDefault?.latitude
            ? Number(tripsValue.pickupAddressDefault?.latitude)
            : 0,
          longitude: tripsValue.pickupAddressDefault?.longitude
            ? Number(tripsValue.pickupAddressDefault?.longitude)
            : 0,
        },
      };
      rideRequestList.push(filter);
    });

    const tripRulesData: ITripRulesValidationInput = {
      benefitGroupId: wandaOrgId.benefitGroupId,
      rideRequests: rideRequestList,
      tripDate: date ? moment(date).format("YYYY-MM-DD") : "",
      personId: Number(params.serviceUserId),
    };

    tripRulesValidation.mutate(tripRulesData);
  };

  const handleCancelWheelchairModal = () => {
    setIsRemoveWheelchair(!isRemoveWheelchair);
  };

  const handleCancelNotPastTripModal = () => {
    setIsNotPastTrip(false);
  };

  const handleContinueNotPastTripModal = () => {
    setIsNotPastTrip(false);
    history.goBack();
  };

  const handleWillCallChanged = (index: number) => {
    const newTrip = [...trips];
    const isCallselected = newTrip[index].isWillCall ? false : true;
    newTrip[index].isWillCall = isCallselected;
    if (isCallselected) {
      newTrip[index].scheduleTime = undefined;
      newTrip[index].areTimesChronological = false;
      newTrip[index].tripSelectedTypeDefault = TripType.LeaveAt;
    }
    setTrips(newTrip);
    const warnTrips = tripScheduleWarningMessage(trips);
    setTrips(warnTrips);
  };

  const handleReturnTripNotCancelBtnClicked = () => {
    setIsNotPastTripStatus(false)
    setIsReturnTripNotAdded(!isReturnTripNotAdded);
  };

  const handleReturnTripAdded = () => {
    setIsNotPastTripStatus(false)
    setIsReturnTripNotAdded(!isReturnTripNotAdded);
    handleAddTrip("return");
  };

  const handleReturnTripContinueAdded = () => {
    setIsNotPastTripStatus(false);
    setIsReturnTripNotAdded(!isReturnTripNotAdded);
    handleAddTrip("continue", true);
  };

  const handleContinueWheelchairModal = async () => {
    try {
      const newUpdatedDate: WheelchairUpdate = {
        personId: Number(params.serviceUserId),
        personAttributesKeyId: serviceUserData.data
          ? serviceUserData.data.wandaAccommodations.personAttributesKeyId
          : 0,
        wandaAccommodations: {
          wheelChairTravel: false,
          wheelChairPresent: false,
          wcToVehicleTransfer: false,
          personAttributesKeyId: serviceUserData.data
            ? serviceUserData.data.wandaAccommodations.personAttributesKeyId
            : 0,
        },
      };
      await UpdatePersonAttributesKey(newUpdatedDate);

      const updatedTrips = trips.map((trip) => ({
        ...trip,
        wheelChairTravel: false,
      }));
      setTrips(updatedTrips);
      setIsRemoveWheelchair(!isRemoveWheelchair);
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to update Person Attribute Key.",
      });
    }
  };

  const checkRequiredFields = (trip: TTrip): boolean => {
    let result: boolean = false;
    if (trip.isWillCall !== undefined && trip.isWillCall) {
      if (
        trip.tripSelectedTypeDefault !== undefined &&
        trip.pickupAddressDefault !== undefined &&
        trip.pickupAddressDefault.city !== undefined &&
        trip.pickupAddressDefault.city !== "" &&
        trip.pickupAddressDefault.state !== undefined &&
        trip.pickupAddressDefault.state !== "" &&
        trip.pickupAddressDefault.zip !== undefined &&
        trip.pickupAddressDefault.zip !== "" &&
        trip.dropOffAddressDefault !== undefined &&
        trip.dropOffAddressDefault.city !== undefined &&
        trip.dropOffAddressDefault.city !== "" &&
        trip.dropOffAddressDefault.state !== undefined &&
        trip.dropOffAddressDefault.state !== "" &&
        trip.dropOffAddressDefault.zip !== undefined &&
        trip.dropOffAddressDefault.zip !== ""
      ) {
        result = true;
      }
    } else {
      if (
        trip.tripSelectedTypeDefault !== undefined &&
        trip.scheduleTime !== undefined &&
        trip.scheduleTime !== "" &&
        trip.pickupAddressDefault !== undefined &&
        trip.pickupAddressDefault.city !== undefined &&
        trip.pickupAddressDefault.city !== "" &&
        trip.pickupAddressDefault.state !== undefined &&
        trip.pickupAddressDefault.state !== "" &&
        trip.pickupAddressDefault.zip !== undefined &&
        trip.pickupAddressDefault.zip !== "" &&
        trip.dropOffAddressDefault !== undefined &&
        trip.dropOffAddressDefault.city !== undefined &&
        trip.dropOffAddressDefault.city !== "" &&
        trip.dropOffAddressDefault.state !== undefined &&
        trip.dropOffAddressDefault.state !== "" &&
        trip.dropOffAddressDefault.zip !== undefined &&
        trip.dropOffAddressDefault.zip !== "" &&
        !trip.areTimesChronological
      ) {
        result = true;
      }
    }

    return result;
  };

  const handleEnabler = () => {
    if (trips.length === 1) {
      const [trip] = trips;
      if (checkRequiredFields(trip) && trips[0].id != 2 && isValidationError) {
        setBtnAllEnable({
          ...btnAllEnable,
          stopBtn: false,
          returnBtn: false,
          continueBtn: false,
          maxAdded: undefined,
        });
      } else if (checkRequiredFields(trip) && trips[0].id === 2) {
        setBtnAllEnable({
          ...btnAllEnable,
          stopBtn: false,
          returnBtn: false,
          continueBtn: false,
          maxAdded: undefined,
          skipReturnTrip: false,
          addReturnTrip: true,
        });
      } else {
        setBtnAllEnable({
          ...btnAllEnable,
          stopBtn: true,
          returnBtn: true,
          continueBtn: true,
          maxAdded: undefined,
          addReturnTrip: true,
        });
      }
    } else if (trips.length === 2) {
      const [Trip0, currentTrip] = trips;
      if (checkRequiredFields(Trip0) && checkRequiredFields(currentTrip)) {
        if (
          currentTrip.tripType === TripTypesButton.AddStop ||
          trips[0].id === 2
        ) {
          setBtnAllEnable({
            ...btnAllEnable,
            returnBtn: false,
            stopBtn: false,
            continueBtn: false,
            maxAdded: undefined,
            skipReturnTrip: false,
            addReturnTrip: true,
          });
        } else if (currentTrip.tripType === TripTypesButton.ReturnTrip) {
          const isOneStopAdded =
            trips.filter((item) => item.tripType === TripTypesButton.AddStop)
              .length === 1 && trips[0].id != 2
              ? true
              : false;

          setBtnAllEnable({
            ...btnAllEnable,
            stopBtn: true,
            returnBtn: isOneStopAdded ? true : false,
            continueBtn: false,
            maxAdded: true,
            skipReturnTrip: isOneStopAdded,
            addReturnTrip: true,
          });
        }
      } else {
        setBtnAllEnable({
          ...btnAllEnable,
          returnBtn: true,
          stopBtn: true,
          continueBtn: true,
          maxAdded: true,
          skipReturnTrip: true,
        });
      }
    } else if (trips.length === 3) {
      const [Trip0, Trip1, currentTrip] = trips;
      if (
        checkRequiredFields(Trip0) &&
        checkRequiredFields(Trip1) &&
        checkRequiredFields(currentTrip)
      ) {
        const isTwoStopAdded =
          trips.filter((item) => item.tripType === TripTypesButton.AddStop)
            .length === 2
            ? true
            : false;
        setBtnAllEnable({
          ...btnAllEnable,
          stopBtn: false,
          returnBtn: isTwoStopAdded ? true : false,
          continueBtn: false,
          maxAdded: true,
          skipReturnTrip: isTwoStopAdded,
          addReturnTrip: false,
        });
      } else {
        setBtnAllEnable({
          ...btnAllEnable,
          stopBtn: true,
          returnBtn: true,
          continueBtn: true,
          maxAdded: undefined,
        });
      }
    }
  };

  useEffect(() => {
    handleEnabler();
  }, [trips, uspsFieldValidationError]);

  useEffect(() => {
    if (isRebook) {
      fetchServiceArea(rebookTrip[0]?.pickupAddressDefault?.zip, rebookTrip);
      setTrips(rebookTrip);
      setIsRebook(false);
    } else if (isModeSelectionBackBtnClicked) {
      setTrips(tripCreationDetails);
      fetchServiceArea(
        tripCreationDetails[0]?.pickupAddressDefault?.zip,
        tripCreationDetails
      );
    }
  }, []);

  const handleFetchCostDetails = async (trips: TTrip[]) => {
    const tripCostRequestList: ITripCostRequest[] = [];
    trips.forEach((record: TTrip) => {
      let filter: ITripCostRequest = {
        dropOffAddress: {
          address1: record.dropOffAddressDefault?.address1
            ? record.dropOffAddressDefault?.address1.trim()
            : "",
          address2: record.dropOffAddressDefault?.address2
            ? record.dropOffAddressDefault?.address2.trim()
            : "",
          city: record.dropOffAddressDefault?.city
            ? record.dropOffAddressDefault?.city.trim()
            : "",
          state: record.dropOffAddressDefault?.state
            ? record.dropOffAddressDefault?.state.trim()
            : "",
          zipCode: record.dropOffAddressDefault?.zip
            ? record.dropOffAddressDefault?.zip.trim()
            : "",
          latitude: record.dropOffAddressDefault?.latitude
            ? Number(record.dropOffAddressDefault?.latitude)
            : 0,
          longitude: record.dropOffAddressDefault?.longitude
            ? Number(record.dropOffAddressDefault?.longitude)
            : 0,
        },
        pickupAddress: {
          address1: record.pickupAddressDefault?.address1
            ? record.pickupAddressDefault?.address1.trim()
            : "",
          address2: record.pickupAddressDefault?.address2
            ? record.pickupAddressDefault?.address2.trim()
            : "",
          city: record.pickupAddressDefault?.city
            ? record.pickupAddressDefault?.city.trim()
            : "",
          state: record.pickupAddressDefault?.state
            ? record.pickupAddressDefault?.state.trim()
            : "",
          zipCode: record.pickupAddressDefault?.zip
            ? record.pickupAddressDefault?.zip.trim()
            : "",
          latitude: record.pickupAddressDefault?.latitude
            ? Number(record.pickupAddressDefault?.latitude)
            : 0,
          longitude: record.pickupAddressDefault?.longitude
            ? Number(record.pickupAddressDefault?.longitude)
            : 0,
        },
        distanceInMiles: record.distanceInMiles?.toString(),
      };
      tripCostRequestList.push(filter);
    });
    getCostDetailsByAddressList.mutate(tripCostRequestList);
    setIsLoadingCostDetails(true);
  };

  const getCostDetailsByAddressList = useMutation(
    async (
      tripCostRequestList: ITripCostRequest[]
    ): Promise<ITripCostResponse> =>
      GetCostDetailsByAddressList(
        tripCostRequestList,
        caregiverSummaryInfo.personId
      ),
    {
      onError: () => {
        notification.error({
          message: "Error",
          description:
            "Failed to calculate cost details for pickup and dropoff address.",
        });
      },
      onSuccess: (data) => {
        if (data) {
          let i = 0;
          data.wandaTripCost.forEach((x: WandaTripCost) => {
            trips[i].distanceInMiles = x.distanceInMiles.toFixed(2);
            trips[i].WandaCostDetail = x.costDetails;
            i++;
          });

          const costInfo = {
            wavExtraCost: Number(
              (data.totalWaveCost - data.totalStandardCost).toFixed(2)
            ),
            wandaPlusDiscount: data.wandaPlusDiscount / 100,
            wandaMarketSourceSystemId: data.wandaMarketSourceSystemId,
          };
          setWandaTripInfo(costInfo);
          setIsLoadingCostDetails(false);
        } else {
          notification.error({
            message: "Error",
            description:
              "Failed to calculate cost details for pickup and dropoff address.",
          });
        }
      },
    }
  );

  return width > GeneralDefinition.MobileView ? (
    <Row>
      {isRemoveWheelchair && serviceUserData.data && (
        <RemoveWheelchairPopup
          isRemoveWheelchair={isRemoveWheelchair}
          handleCancelWheelchairModal={handleCancelWheelchairModal}
          handleContinueWheelchairModal={handleContinueWheelchairModal}
          serviceUserData={serviceUserData.data}
        />
      )}
      {isReturnTripNotAdded && (
        <NoReturnTripPopup
          isReturnTripNotAdded={isReturnTripNotAdded}
          btnAllEnable={btnAllEnable}
          handleReturnTripAdded={handleReturnTripAdded}
          handleReturnTripContinueAdded={handleReturnTripContinueAdded}
          handleReturnTripNotCancelBtnClicked={
            handleReturnTripNotCancelBtnClicked
          }
        />
      )}
      {tripRulesValidation.isLoading ? (
        <GoogleLoader />
      ) : (
        <Col span={24}>
          <Row>
            <Col offset={4}>
              <Space>
                <Image preview={false} src={backArrowIcon} />
                <Typography.Text
                  className={Styles.backButton}
                  onClick={handleBackBtnClick}
                >
                  Back
                </Typography.Text>
              </Space>
            </Col>
          </Row>
          <Row justify="center" className={Styles.headerTitleSection}>
            <Col>
              <Space direction="vertical" align="center">
                <Typography.Text className={Styles.titleTextBold}>
                  TRANSPORTATION SETUP
                </Typography.Text>
                <Typography.Text className={Styles.titleTextBoldBlue}>
                  {`${serviceUserData.isSuccess &&
                    serviceUserData.data.firstName}’s`}{" "}
                  {date !== null && date.includes("/")
                    ? date
                    : moment(date, "ddd MMM DD YYYY HH:mm:ss [GMT] ZZ").format(
                        "MM/DD/YYYY"
                      )}{" "}
                  trip:
                </Typography.Text>
              </Space>
            </Col>
          </Row>
          {trips.length &&
            trips.map((trip, i) => (
              <Trip
                key={i}
                index={i}
                trip={trip}
                onTimechange={handleChangeTime}
                onClearTime={handleClearTime}
                onAddressChange={handleChangeAddress}
                onTripTypeChange={handleChangeTrip}
                onFieldsClear={handleClearFields}
                onDelete={handleTripDelete}
                onWheelChairChange={handleCancelWheelchairModal}
                onWillCallChange={handleWillCallChanged}
                onFieldChange={handleFieldChange}
                onClearinstructionField={handleClearinstructionField}
                onDriverInstrctionValidation={handleDriverInstrctionValidation}
                tripLength={trips.length}
              />
            ))}
          <Row justify="center" className={Styles.tripButtonSection}>
            <Col>
              <Space>
                {btnAllEnable?.maxAdded && !btnAllEnable.stopBtn ? (
                  <Tooltip
                    title={"You've reached the maximum amount of stops."}
                  >
                    <Button
                      disabled={btnAllEnable.stopBtn}
                      className={Styles.buttonThemeAddTripDisabled}
                    >
                      Add a stop
                    </Button>
                  </Tooltip>
                ) : (
                  <Button
                    disabled={btnAllEnable.stopBtn}
                    className={
                      btnAllEnable.stopBtn
                        ? Styles.buttonThemeDisabled
                        : Styles.buttonTheme
                    }
                    onClick={() => handleAddTrip("stop")}
                  >
                    Add a stop
                  </Button>
                )}

                {btnAllEnable?.maxAdded &&
                !btnAllEnable.returnBtn &&
                !btnAllEnable?.skipReturnTrip ? (
                  <Tooltip title={"You have already added a return trip."}>
                    <Button
                      disabled={btnAllEnable.returnBtn}
                      className={Styles.buttonThemeCustomDisabled}
                    >
                      Add a return trip
                    </Button>
                  </Tooltip>
                ) : (
                  <Button
                    disabled={btnAllEnable.returnBtn}
                    className={
                      btnAllEnable.returnBtn
                        ? Styles.buttonThemeReturnDisabled
                        : Styles.buttonThemeReturn
                    }
                    onClick={() => handleAddTrip("return")}
                  >
                    Add a return trip
                  </Button>
                )}

                <Button
                  disabled={btnAllEnable.continueBtn}
                  className={
                    btnAllEnable.continueBtn
                      ? Styles.buttonThemeBlueDisabled
                      : Styles.buttonThemeBlue
                  }
                  onClick={() => handleAddTrip("continue")}
                >
                  Continue
                </Button>
              </Space>
            </Col>
          </Row>
        </Col>
      )}
      <div>
        <NotCoverageZipPopup
          isNotCovergeZip={isNotCovergeZip}
          handleCancelCoverage={handleCancelCoverage}
        />
      </div>
      <div>
        <SameAddressPopup
          isNotSameAddress={isNotSameAddress}
          handleCancelSameAddress={handleCancelSameAddress}
        />
      </div>
      <div>
        <NotPastTripPopup
          isNotPastTrip={isNotPastTrip}
          handleCancelNotPastTripModal={handleCancelNotPastTripModal}
          handleContinueNotPastTripModal={handleContinueNotPastTripModal}
        />
      </div>
    </Row>
  ) : (
    <Row>
      {isRemoveWheelchair && serviceUserData.data && (
        <RemoveWheelchairPopup
          isRemoveWheelchair={isRemoveWheelchair}
          handleCancelWheelchairModal={handleCancelWheelchairModal}
          handleContinueWheelchairModal={handleContinueWheelchairModal}
          serviceUserData={serviceUserData.data}
        />
      )}
      {isReturnTripNotAdded && (
        <NoReturnTripPopup
          isReturnTripNotAdded={isReturnTripNotAdded}
          btnAllEnable={btnAllEnable}
          handleReturnTripAdded={handleReturnTripAdded}
          handleReturnTripContinueAdded={handleReturnTripContinueAdded}
          handleReturnTripNotCancelBtnClicked={
            handleReturnTripNotCancelBtnClicked
          }
        />
      )}
      {tripRulesValidation.isLoading ? (
        <GoogleLoader />
      ) : (
        <Col span={24}>
          <Row className={Styles.backButtonSection}>
            <Col offset={2}>
              <Space>
                <Image preview={false} src={backArrowIcon} />
                <Typography.Text
                  className={Styles.backButton}
                  onClick={handleBackBtnClick}
                >
                  Back
                </Typography.Text>
              </Space>
            </Col>
          </Row>
          <Row className={Styles.headerTitleSection}>
            <Col offset={2}>
              <Space direction="vertical">
                <Typography.Text className={Styles.titleTextBold}>
                  TRANSPORTATION SETUP
                </Typography.Text>
                <Typography.Text className={Styles.titleTextBoldBlue}>
                  {`${serviceUserData.isSuccess &&
                    serviceUserData.data.firstName}’s`}{" "}
                  {date !== null && date.includes("/")
                    ? date
                    : moment(date, "ddd MMM DD YYYY HH:mm:ss [GMT] ZZ").format(
                        "MM/DD/YYYY"
                      )}{" "}
                  trip:
                </Typography.Text>
              </Space>
            </Col>
          </Row>
          {trips.map((trip, i) => (
            <Trip
              key={i}
              index={i}
              trip={trip}
              onTimechange={handleChangeTime}
              onClearTime={handleClearTime}
              onAddressChange={handleChangeAddress}
              onTripTypeChange={handleChangeTrip}
              onFieldsClear={handleClearFields}
              onDelete={handleTripDelete}
              onWheelChairChange={handleCancelWheelchairModal}
              onWillCallChange={handleWillCallChanged}
              onFieldChange={handleFieldChange}
              onClearinstructionField={handleClearinstructionField}
              onDriverInstrctionValidation={handleDriverInstrctionValidation}
              tripLength={trips.length}
            />
          ))}
          <Row justify="center" className={Styles.tripButtonSection}>
            <Col>
              <Space direction="vertical">
                {btnAllEnable?.maxAdded && !btnAllEnable.stopBtn ? (
                  <Tooltip
                    trigger="hover"
                    title={"You've reached the maximum amount of stops."}
                  >
                    <Button
                      disabled={btnAllEnable.stopBtn}
                      className={Styles.buttonThemeCustomDisabled}
                    >
                      Add a stop
                    </Button>
                  </Tooltip>
                ) : (
                  <Button
                    disabled={btnAllEnable.stopBtn}
                    className={
                      btnAllEnable.stopBtn
                        ? Styles.buttonThemeCustomForReturnDisabled
                        : Styles.buttonThemeCustomForReturn
                    }
                    onClick={() => handleAddTrip("stop")}
                  >
                    Add a stop
                  </Button>
                )}

                {btnAllEnable?.maxAdded &&
                !btnAllEnable.returnBtn &&
                !btnAllEnable?.skipReturnTrip ? (
                  <Tooltip title={"You have already added a return trip."}>
                    <Button
                      disabled={btnAllEnable.returnBtn}
                      className={Styles.buttonThemeCustomDisabled}
                    >
                      Add a return trip
                    </Button>
                  </Tooltip>
                ) : (
                  <Button
                    disabled={btnAllEnable.returnBtn}
                    className={
                      btnAllEnable.returnBtn
                        ? Styles.buttonThemeCustomForReturnDisabled
                        : Styles.buttonThemeCustomForReturn
                    }
                    onClick={() => handleAddTrip("return")}
                  >
                    Add a return trip
                  </Button>
                )}

                <Button
                  disabled={btnAllEnable.continueBtn}
                  className={
                    btnAllEnable.continueBtn
                      ? Styles.buttonThemeBlueMobileDisabled
                      : Styles.buttonThemeBlueMobile
                  }
                  onClick={() => handleAddTrip("continue")}
                >
                  Continue
                </Button>
              </Space>
            </Col>
          </Row>
        </Col>
      )}
      <div>
        <NotCoverageZipPopup
          isNotCovergeZip={isNotCovergeZip}
          handleCancelCoverage={handleCancelCoverage}
        />
      </div>
      <div>
        <SameAddressPopup
          isNotSameAddress={isNotSameAddress}
          handleCancelSameAddress={handleCancelSameAddress}
        />
      </div>
      <div>
        <NotPastTripPopup
          isNotPastTrip={isNotPastTrip}
          handleCancelNotPastTripModal={handleCancelNotPastTripModal}
          handleContinueNotPastTripModal={handleContinueNotPastTripModal}
        />
      </div>
    </Row>
  );
};

export default TripSchedule;
